import {Timing} from '@aboutyou/mlm';
import {usePathname, useSearch} from '@aboutyou/router';
import {useCOTracking} from '@aboutyou/tracking';
import {useCallback, useEffect, useRef} from 'react';
import {CHECKOUT_URL_PARAMS} from '../../../features/Checkout/constants';
import {updateUrlParams} from '../../../shared-deprecated/updateUrlParams';
import {getIsBnplOffered} from '../hooks/getIsBnplOffered';
import {CheckoutExperiment} from '../hooks/useCheckoutEntryDetails';

export const useReportTimings = ({
  entryTimeStamp,
  clientEntryTimeStamp,
  timing,
  isExpressPaymentMethod,
  experiments,
}: {
  entryTimeStamp?: string | null;
  clientEntryTimeStamp?: string | null;
  timing: Timing;
  isExpressPaymentMethod?: boolean;
  experiments?: CheckoutExperiment[];
}) => {
  const {track} = useCOTracking();
  const webComponentRenderTiming = useRef(new Timing()).current;
  const hasLogged = useRef(false);
  const endTrigger = isExpressPaymentMethod
    ? 'express-checkout-review-load'
    : 'checkout-load';

  const eventType = 'CheckoutLoadTiming';

  const isBnplOffered = getIsBnplOffered(experiments);

  const reportTimings = useCallback(() => {
    if (hasLogged.current) {
      return;
    }

    track({
      type: eventType,
      endTrigger,
      startTrigger: isExpressPaymentMethod
        ? 'express-checkout-page-entry'
        : 'checkout-page-entry',
      duration: timing.end(),
      is_bnpl_offered: isBnplOffered,
    });

    track({
      type: eventType,
      endTrigger,
      startTrigger: isExpressPaymentMethod
        ? 'express-web-component-render-start'
        : 'web-component-render-start',
      duration: webComponentRenderTiming.end(),
      is_bnpl_offered: isBnplOffered,
    });

    const entryTimeInMilliSeconds = entryTimeStamp
      ? Date.parse(entryTimeStamp) // milliseconds since epoch
      : undefined;
    // express-checkout-authorization
    if (entryTimeInMilliSeconds && entryTimeInMilliSeconds > 0) {
      track({
        type: eventType,
        endTrigger,
        startTrigger: isExpressPaymentMethod
          ? 'express-payment-method-authorization'
          : 'go-to-checkout-click',
        duration: Date.now() - entryTimeInMilliSeconds,
        is_bnpl_offered: isBnplOffered,
      });
    }

    const clientEntryTimeInMilliSeconds = clientEntryTimeStamp
      ? Date.parse(clientEntryTimeStamp) // milliseconds since epoch
      : undefined;

    if (clientEntryTimeInMilliSeconds && clientEntryTimeInMilliSeconds > 0) {
      track({
        type: eventType,
        endTrigger,
        startTrigger: 'before-app-go-to-checkout-click',
        duration: Date.now() - clientEntryTimeInMilliSeconds,
        is_bnpl_offered: isBnplOffered,
      });
    }

    hasLogged.current = true;
  }, [
    clientEntryTimeStamp,
    endTrigger,
    entryTimeStamp,
    isExpressPaymentMethod,
    timing,
    track,
    webComponentRenderTiming,
    isBnplOffered,
  ]);

  useRemoveClientEntryTimeStampFromUrl();

  return {reportTimings};
};

/**
 * This hook removes the entryTimeStamp and clientEntryTimeStamp from the URL without a rerender.
 *
 * This is necessary because the entryTimeStamp and clientEntryTimeStamp are only used for one time tracking on the first page load.
 * When the user navigates to external urls (like paypal payment) and navigates back to the checkout, the entryTimeStamp and clientEntryTimeStamp should not be used for tracking again
 * because the server entryTimeStamp would be outdated at this point and we would track a very high and wrong duration.
 * This is also the case when the user simply reloads the page.
 * In these cases, the entryTimeStamp and clientEntryTimeStamp should be removed from the URL and using window.history.replaceState is the best way to do this without a rerender.
 */
const useRemoveClientEntryTimeStampFromUrl = () => {
  const search = useSearch();
  const pathname = usePathname();
  const hasRemoved = useRef(false);

  const updatedSearch = updateUrlParams({
    search,
    update: new Map([
      [CHECKOUT_URL_PARAMS.entryTimeStamp, undefined],
      [CHECKOUT_URL_PARAMS.clientEntryTimeStamp, undefined],
    ]),
  });
  const queryString = updatedSearch ? `?${updatedSearch}` : updatedSearch;

  // eslint-disable-next-line local-rules/prefer-use-history
  useEffect(() => {
    if (hasRemoved.current) return;

    if (
      search.includes(CHECKOUT_URL_PARAMS.entryTimeStamp) ||
      search.includes(CHECKOUT_URL_PARAMS.clientEntryTimeStamp)
    ) {
      window.history.replaceState(null, '', `${pathname}${queryString}`);
      hasRemoved.current = true;
    }
  }, [pathname, queryString, search]);
};
